import React from "react";

import { mdiUploadCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import classnames from "classnames";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import VideoPreview from "./VideoPreview";
import { Button } from "../ui/button";

import type { JSX } from "react";

export type VideoUploadZoneState =
  | { status: "empty"; isDefaultLanguage: boolean }
  | {
      status: "inherited";
      defaultLanguageName: string;
      filename: string;
      videoUrl: string;
    }
  | { status: "queued" }
  | { status: "uploading"; progress: number }
  | { status: "error" }
  | {
      status: "ready";
      filename: string;
      videoUrl: string;
      isDeletable: boolean;
    };

interface VideoUploadZoneProps {
  state: VideoUploadZoneState;
  languageName: string;
  onSelectFile: (file: File) => void;
  onRetry: () => void;
  onDelete: () => void;
}

const VIDEO_ACCEPT = { "video/mp4": [".mp4"] };

const ACTION_CLASSNAMES =
  "bg-primary/5 text-primary hover:bg-primary/10 rounded-[0.15625rem] px-[0.3125rem] py-[0.15625rem] text-[0.78125rem] font-medium transition-colors";

/**
 * The video slot for one language. Presentational: it knows nothing about the
 * form or the upload queue, and derives no state of its own — it renders the
 * one it is handed.
 *
 * Its copy, however, is read from the `editor-advertisings` namespace: this is
 * the only consumer today. Lift the namespace to a prop before wiring a second
 * one, rather than assuming the component is already domain agnostic.
 */
export default function VideoUploadZone({
  state,
  languageName,
  onSelectFile,
  onRetry,
  onDelete,
}: VideoUploadZoneProps): JSX.Element {
  const { t } = useTranslation("editor-advertisings");

  const isLocked = state.status === "queued" || state.status === "uploading";

  const { getRootProps, getInputProps, open, isDragAccept, isDragReject } =
    useDropzone({
      accept: VIDEO_ACCEPT,
      multiple: false,
      // noClick is required, not cosmetic: the root wraps the <video> element,
      noClick: true,
      noKeyboard: true,
      // While a job is in flight, dropping again would silently replace it (one
      // job per scope), so the zone is locked until it settles.
      disabled: isLocked,
      onDrop: (fileList: File[]): void => {
        const [file] = fileList;

        if (file) onSelectFile(file);
      },
    });

  const rootClassNames = classnames(
    "flex max-w-3xl flex-col gap-2.5 rounded-[0.3125rem]",
    {
      "ring-2 ring-status-active": isDragAccept,
      "ring-2 ring-destructive": isDragReject,
    },
  );

  function renderDropTarget(label: string, hint?: string): JSX.Element {
    return (
      <button
        className="border-input hover:bg-muted flex aspect-video w-full flex-col items-center justify-center gap-[0.3125rem] rounded-[0.3125rem] border border-dashed p-[1.25rem] text-center transition-colors"
        onClick={open}
        type="button"
      >
        <Icon
          className="text-primary"
          path={mdiUploadCircleOutline}
          size={1.25}
        />
        <span className="text-primary font-semibold">{label}</span>
        <span className="text-muted-foreground text-[0.78125rem]">
          {t("videoUpload.accept")}
        </span>
        {hint ? (
          <span className="text-muted-foreground text-[0.78125rem]">
            {hint}
          </span>
        ) : null}
      </button>
    );
  }

  function renderContent(): JSX.Element {
    switch (state.status) {
      case "queued":
        return (
          <div className="bg-muted text-muted-foreground flex aspect-video w-full items-center justify-center rounded-[0.3125rem]">
            {t("videoUpload.queued")}
          </div>
        );
      case "uploading":
        return (
          <div className="bg-muted flex aspect-video w-full flex-col items-center justify-center gap-[0.625rem] rounded-[0.3125rem] px-[1.25rem]">
            <div className="bg-border-strong h-[0.3125rem] w-full overflow-hidden rounded-full">
              <div
                className="bg-primary h-full rounded-full transition-all"
                style={{ width: `${state.progress}%` }}
              />
            </div>
            <p className="text-muted-foreground text-[0.78125rem]">
              {t("videoUpload.uploading")} {state.progress}%
            </p>
          </div>
        );
      case "error":
        return (
          <div className="border-destructive flex aspect-video w-full flex-col items-center justify-center gap-[0.625rem] rounded-[0.3125rem] border border-dashed">
            <p className="text-destructive">{t("videoUpload.error")}</p>
            <button
              className={ACTION_CLASSNAMES}
              onClick={onRetry}
              type="button"
            >
              {t("videoUpload.retry")}
            </button>
          </div>
        );
      case "ready":
        return (
          <React.Fragment>
            <VideoPreview videoUrl={state.videoUrl} />
            <div className="flex items-center gap-[0.625rem]">
              <span className="text-muted-foreground min-w-0 flex-1 truncate text-[0.78125rem]">
                {state.filename}
              </span>
              <button
                className={ACTION_CLASSNAMES}
                onClick={open}
                type="button"
              >
                {t("videoUpload.replace")}
              </button>
              {state.isDeletable ? (
                <button
                  className="bg-destructive/10 text-destructive hover:bg-destructive/20 rounded-[0.15625rem] px-[0.3125rem] py-[0.15625rem] text-[0.78125rem] font-medium transition-colors"
                  onClick={onDelete}
                  type="button"
                >
                  {t("videoUpload.delete")}
                </button>
              ) : null}
            </div>
          </React.Fragment>
        );
      case "inherited":
        return (
          <React.Fragment>
            <VideoPreview
              isDimmed
              videoUrl={state.videoUrl}
              badgeLabel={t("videoUpload.inheritedBadge", {
                language: state.defaultLanguageName,
              })}
            />
            <div className="flex">
              <div className="flex flex-1/2 shrink-0 flex-col gap-2">
                <p className="font-medium">
                  {t("videoUpload.inheritedTitle", { language: languageName })}
                </p>
                <p className="text-muted-foreground text-[0.78125rem]">
                  {t("videoUpload.inheritedHint", {
                    defaultLanguage: state.defaultLanguageName,
                  })}
                </p>
              </div>
              <Button onClick={open}>
                <Icon path={mdiUploadCircleOutline} size={1} />
                {t("videoUpload.setForLanguage", { language: languageName })}
              </Button>
            </div>
          </React.Fragment>
        );
      case "empty":
        return renderDropTarget(
          t("videoUpload.dropzone"),
          state.isDefaultLanguage ? t("videoUpload.defaultHint") : undefined,
        );
      default: {
        // Exhaustiveness guard: a new state must be rendered explicitly.
        const exhaustiveCheck: never = state;

        return exhaustiveCheck;
      }
    }
  }

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div {...getRootProps({ className: rootClassNames })}>
      {renderContent()}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <input {...getInputProps()} />
    </div>
  );
}
